import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CycleReportPanel } from "../components/ecoles/CycleReportPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const SECTORS = { primaire: "Primaire", secondaire: "Secondaire", postsecondaire: "Postsecondaire / Cégep", anglophone: "Anglophone", autre: "Autre / CS" };
const SCHOOL_STATUS = {
  prospect: { l: "Prospect", c: "bg-zinc-700 text-zinc-200" },
  contacte: { l: "Contacté", c: "bg-blue-500/20 text-blue-300" },
  interesse: { l: "Intéressé", c: "bg-amber-500/20 text-amber-300" },
  pilote: { l: "Pilote", c: "bg-violet-500/20 text-violet-300" },
  partenaire: { l: "Partenaire", c: "bg-emerald-500/20 text-emerald-300" },
  refus: { l: "Refus", c: "bg-red-500/20 text-red-300" },
};
const OFFER_STATUS = {
  brouillon: "Brouillon", envoyee: "Envoyée", relance: "Relance", rencontre: "Rencontre",
  approuvee: "Approuvée", refusee: "Refusée", pilote: "Pilote actif", reconduite: "Reconduite",
};
const SESSION_STATUS = { planifie: "Planifié", actif: "Actif", termine: "Terminé", annule: "Annulé" };

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} $`;
const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copié ✓"); };
const openBlob = async (url, filename, download = false) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    if (download) {
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    } else {
      window.open(blobUrl, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  } catch { toast.error("Téléchargement impossible"); }
};

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function ProgrammeEcolesModule() {
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [schools, setSchools] = useState([]);
  const [offers, setOffers] = useState([]);
  const [sessions, setSessions] = useState([]);

  const loadAll = async () => {
    try {
      const [d, s, o, se] = await Promise.all([
        api.get("/ecoles/dashboard"),
        api.get("/ecoles/schools"),
        api.get("/ecoles/offers"),
        api.get("/ecoles/sessions"),
      ]);
      setDash(d.data); setSchools(s.data.schools || []);
      setOffers(o.data.offers || []); setSessions(se.data.sessions || []);
    } catch { toast.error("Chargement impossible"); }
  };
  useEffect(() => { loadAll(); }, []);

  const seedTargets = async () => {
    try {
      const { data } = await api.post("/ecoles/seed-targets");
      toast.success(`${data.created} école(s) cible(s) ajoutée(s)`);
      loadAll();
    } catch { toast.error("Échec"); }
  };

  const tabs = [
    ["dashboard", "📊 Tableau de bord"],
    ["schools", "🏫 Écoles"],
    ["offers", "📄 Offres"],
    ["sessions", "🥋 Sessions & inscriptions"],
    ["pilotes", "🚀 Pilotes actifs"],
    ["kit", "🧰 Kit de vente"],
  ];

  return (
    <div className="space-y-6" data-testid="programme-ecoles-module">
      <div>
        <div className="text-red-400 text-[10px] tracking-[0.4em] mb-1">🎒 DÉVELOPPEMENT SCOLAIRE</div>
        <h1 className="text-2xl font-bold">Programme Écoles</h1>
        <p className="text-zinc-500 text-sm mt-1">Parascolaire kickboxing/Muay Thaï pour les écoles de Val-d'Or — prospection, offres, pilotes, inscriptions.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-zinc-800 pb-2">
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${tab === id ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab dash={dash} onSeed={seedTargets} />}
      {tab === "schools" && <SchoolsTab schools={schools} onChanged={loadAll} onSeed={seedTargets} />}
      {tab === "offers" && <OffersTab offers={offers} schools={schools} onChanged={loadAll} />}
      {tab === "sessions" && <SessionsTab sessions={sessions} schools={schools} onChanged={loadAll} />}
      {tab === "pilotes" && <PilotesTab sessions={sessions} />}
      {tab === "kit" && <KitTab />}
    </div>
  );
}

/* ───────────────── Tableau de bord ───────────────── */
function DashboardTab({ dash, onSeed }) {
  if (!dash) return <div className="text-zinc-500">Chargement…</div>;
  const k = dash.kpis || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="kpi-schools" label="Écoles ciblées" value={k.schools_total ?? 0} />
        <Kpi testid="kpi-contacted" label="Contactées" value={k.schools_contacted ?? 0} accent="text-blue-300" />
        <Kpi testid="kpi-interested" label="Intéressées" value={k.schools_interested ?? 0} accent="text-amber-300" />
        <Kpi testid="kpi-pilots" label="Pilotes actifs" value={k.pilots_active ?? 0} accent="text-violet-300" />
        <Kpi testid="kpi-offers" label="Offres envoyées" value={k.offers_sent ?? 0} />
        <Kpi testid="kpi-enroll" label="Inscriptions" value={k.total_enrollments ?? 0} accent="text-emerald-300" />
        <Kpi testid="kpi-revenue" label="Revenu estimé" value={fmt(k.revenue_estimate)} accent="text-emerald-300" />
        <Kpi testid="kpi-conv" label="Taux conversion" value={`${k.conversion_rate ?? 0} %`} accent="text-red-300" />
      </div>

      {(dash.followups_due || []).length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="followups-due">
          <div className="text-sm font-semibold text-amber-300 mb-2">🔔 Relances à faire</div>
          {dash.followups_due.map((f) => (
            <div key={f.school_id} className="text-sm text-zinc-300">• {f.name} <span className="text-zinc-500">— prévu {f.date}</span></div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
        <div className="text-sm font-semibold text-zinc-300 mb-3">Pipeline des offres</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(dash.pipeline || {}).map(([st, n]) => (
            <div key={st} className="rounded-lg bg-zinc-900 p-2 text-center">
              <div className="text-[10px] text-zinc-500">{OFFER_STATUS[st] || st}</div>
              <div className="font-bold">{n}</div>
            </div>
          ))}
        </div>
      </div>

      {k.schools_total === 0 && (
        <div className="text-center py-8 border border-dashed border-zinc-800 rounded-xl">
          <p className="text-zinc-400 mb-3">Aucune école encore. Démarre avec les cibles connues de Val-d'Or.</p>
          <Button onClick={onSeed} className="bg-red-600 hover:bg-red-500" data-testid="seed-btn-dash">⚡ Ajouter les écoles cibles (CSSOB, Le Carrefour, Golden Valley…)</Button>
        </div>
      )}
    </div>
  );
}

/* ───────────────── Écoles ───────────────── */
const emptySchool = { name: "", sector: "primaire", address: "", city: "Val-d'Or", phone: "", email: "", status: "prospect", notes: "", next_followup: "", contacts: [] };

function SchoolsTab({ schools, onChanged, onSeed }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setEditing({ ...emptySchool })} className="bg-red-600 hover:bg-red-500" data-testid="add-school-btn">+ Nouvelle école</Button>
        <Button onClick={onSeed} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="seed-btn">⚡ Écoles cibles Val-d'Or</Button>
        <Button onClick={() => openBlob("/ecoles/export/schools.csv", "ecoles_valdor.csv", true)} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="export-csv-btn">📤 Export CSV</Button>
        <Button onClick={() => openBlob("/ecoles/brochure.pdf", "brochure.pdf", false)} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="brochure-pdf-btn">📄 Brochure PDF (FR)</Button>
        <Button onClick={() => openBlob("/ecoles/brochure.pdf?lang=en", "brochure_EN.pdf", false)} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="brochure-pdf-en-btn">📄 Brochure PDF (EN)</Button>
      </div>
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-xs">
            <tr><th className="text-left px-3 py-2">École</th><th className="text-left px-3 py-2">Secteur</th><th className="text-left px-3 py-2">Contact</th><th className="text-left px-3 py-2">Relance</th><th className="text-left px-3 py-2">Statut</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {schools.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-500">Aucune école.</td></tr>
            ) : schools.map((s) => (
              <tr key={s.id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`school-row-${s.id}`}>
                <td className="px-3 py-2 font-medium">{s.name}<div className="text-[11px] text-zinc-500">{s.city}{s.phone ? ` · ${s.phone}` : ""}</div></td>
                <td className="px-3 py-2 text-zinc-400">{SECTORS[s.sector] || s.sector}</td>
                <td className="px-3 py-2 text-zinc-400">{s.email || (s.contacts?.[0]?.email) || "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{s.next_followup || "—"}</td>
                <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${SCHOOL_STATUS[s.status]?.c || "bg-zinc-700"}`}>{SCHOOL_STATUS[s.status]?.l || s.status}</span></td>
                <td className="px-3 py-2 text-right"><Button size="sm" onClick={() => setEditing(s)} className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700" data-testid={`edit-school-${s.id}`}>Ouvrir</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <SchoolDialog school={editing} onClose={() => setEditing(null)} onChanged={onChanged} />}
    </div>
  );
}

function SchoolDialog({ school, onClose, onChanged }) {
  const [f, setF] = useState({ ...emptySchool, ...school, contacts: school.contacts || [] });
  const isNew = !school.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nom requis"); return; }
    try {
      const payload = { ...f, next_followup: f.next_followup || null };
      if (isNew) await api.post("/ecoles/schools", payload);
      else await api.put(`/ecoles/schools/${school.id}`, payload);
      toast.success("École enregistrée ✓"); onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async () => {
    if (!window.confirm("Supprimer cette école (et ses offres/sessions) ?")) return;
    try { await api.delete(`/ecoles/schools/${school.id}`); toast.success("Supprimée"); onChanged(); onClose(); }
    catch { toast.error("Erreur"); }
  };
  const sendOffer = async () => {
    if (!window.confirm(`Envoyer le courriel d'approche à ${f.name} ?`)) return;
    try {
      const { data } = await api.post(`/ecoles/schools/${school.id}/send-offer`);
      toast.success(`✉️ Offre envoyée à ${data.to}`); onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };
  const genPublicLink = async () => {
    try {
      const { data } = await api.post(`/ecoles/schools/${school.id}/public-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success("🔗 Lien d'inscription copié — partage-le à l'école / aux parents");
    } catch { toast.error("Erreur"); }
  };
  const addContact = () => set("contacts", [...f.contacts, { name: "", role: "", email: "", phone: "" }]);
  const setContact = (i, k, v) => set("contacts", f.contacts.map((c, j) => j === i ? { ...c, [k]: v } : c));
  const rmContact = (i) => set("contacts", f.contacts.filter((_, j) => j !== i));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="school-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Nouvelle école" : f.name}</DialogTitle>
          <DialogDescription>Fiche école, contacts et statut de prospection.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nom"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="bg-zinc-900 border-zinc-800" data-testid="school-name" /></Field>
          <Field label="Secteur"><Sel value={f.sector} onChange={(v) => set("sector", v)} opts={SECTORS} testid="school-sector" /></Field>
          <Field label="Adresse"><Input value={f.address || ""} onChange={(e) => set("address", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Ville"><Input value={f.city || ""} onChange={(e) => set("city", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Téléphone"><Input value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Courriel"><Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Statut"><Sel value={f.status} onChange={(v) => set("status", v)} opts={Object.fromEntries(Object.entries(SCHOOL_STATUS).map(([k, v]) => [k, v.l]))} testid="school-status" /></Field>
          <Field label="Prochaine relance"><Input type="date" value={f.next_followup || ""} onChange={(e) => set("next_followup", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
        </div>
        <Field label="Notes"><textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm" /></Field>

        <div className="border-t border-zinc-800 pt-3">
          <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold text-zinc-400">Contacts</span>
            <Button size="sm" onClick={addContact} className="h-6 text-[11px] bg-zinc-800 hover:bg-zinc-700" data-testid="add-contact">+ Contact</Button></div>
          {f.contacts.map((c, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 mb-1.5">
              <Input placeholder="Nom" value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <Input placeholder="Rôle" value={c.role} onChange={(e) => setContact(i, "role", e.target.value)} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <Input placeholder="Courriel" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <div className="flex gap-1"><Input placeholder="Tél" value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} className="bg-zinc-900 border-zinc-800 h-8 text-xs" />
                <button onClick={() => rmContact(i)} className="text-red-400 px-1">✕</button></div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          {!isNew && <Button onClick={del} variant="outline" className="border-red-900 text-red-400 hover:bg-red-950 mr-auto" data-testid="delete-school">Supprimer</Button>}
          {!isNew && <Button onClick={() => openBlob(`/ecoles/brochure.pdf?school_id=${school.id}`, "brochure.pdf", false)} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="school-brochure-btn">📄 Brochure FR</Button>}
          {!isNew && <Button onClick={() => openBlob(`/ecoles/brochure.pdf?school_id=${school.id}&lang=en`, "brochure_EN.pdf", false)} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="school-brochure-en-btn">📄 Brochure EN</Button>}
          {!isNew && <Button onClick={genPublicLink} variant="outline" className="border-emerald-800 text-emerald-300 hover:bg-emerald-950" data-testid="public-link-btn">🔗 Lien inscription</Button>}
          {!isNew && <Button onClick={sendOffer} className="bg-blue-600 hover:bg-blue-500" data-testid="send-offer-btn">✉️ Envoyer l'offre</Button>}
          <Button onClick={save} className="bg-red-600 hover:bg-red-500" data-testid="save-school">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────── Offres ───────────────── */
const emptyOffer = { school_id: "", program: "Kickboxing / Muay Thaï", level: "primaire", schedule: "Mardi & jeudi · 16 h 15 – 17 h 15", price: 99, min_students: 10, status: "brouillon", sent_date: "", followup_date: "", notes: "" };

function OffersTab({ offers, schools, onChanged }) {
  const [editing, setEditing] = useState(null);
  const runFollowups = async () => {
    if (!window.confirm("Envoyer une relance par courriel aux écoles dont l'offre est en attente depuis 7+ jours ?")) return;
    try {
      const { data } = await api.post("/ecoles/offers/followups/run?dry_run=false");
      const sent = (data.results || []).filter((r) => r.sent).length;
      toast.success(`📨 ${sent} relance(s) envoyée(s)`); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => setEditing({ ...emptyOffer })} disabled={schools.length === 0} className="bg-red-600 hover:bg-red-500" data-testid="add-offer-btn">+ Nouvelle offre</Button>
        <Button onClick={runFollowups} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="run-followups-btn">📨 Relancer les offres en attente</Button>
      </div>
      {schools.length === 0 && <p className="text-xs text-zinc-500">Ajoute d'abord une école.</p>}
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-xs"><tr><th className="text-left px-3 py-2">École</th><th className="text-left px-3 py-2">Programme</th><th className="text-left px-3 py-2">Niveau</th><th className="text-right px-3 py-2">Prix</th><th className="text-left px-3 py-2">Statut</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {offers.length === 0 ? <tr><td colSpan={6} className="px-3 py-10 text-center text-zinc-500">Aucune offre.</td></tr>
              : offers.map((o) => (
                <tr key={o.id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`offer-row-${o.id}`}>
                  <td className="px-3 py-2 font-medium">{o.school_name}{o.title && <div className="text-xs text-amber-300 font-normal">{o.title}{o.do_not_publish && <span className="ml-1 text-[9px] uppercase tracking-wider border border-amber-500/50 rounded px-1">brouillon interne — ne pas publier</span>}</div>}</td>
                  <td className="px-3 py-2 text-zinc-400">{o.program}</td>
                  <td className="px-3 py-2 text-zinc-400">{SECTORS[o.level] || o.level}</td>
                  <td className="px-3 py-2 text-right">{o.price_note ? <span className="text-xs text-amber-300" title={o.price_note}>à fixer</span> : fmt(o.price)}</td>
                  <td className="px-3 py-2"><span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{OFFER_STATUS[o.status] || o.status}</span></td>
                  <td className="px-3 py-2 text-right"><Button size="sm" onClick={() => setEditing(o)} className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700" data-testid={`edit-offer-${o.id}`}>Ouvrir</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {editing && <OfferDialog offer={editing} schools={schools} onClose={() => setEditing(null)} onChanged={onChanged} />}
    </div>
  );
}

function OfferDialog({ offer, schools, onClose, onChanged }) {
  const [f, setF] = useState({ ...emptyOffer, ...offer });
  const isNew = !offer.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.school_id) { toast.error("Choisis une école"); return; }
    try {
      const payload = { ...f, price: parseFloat(f.price) || 0, min_students: parseInt(f.min_students) || 0, sent_date: f.sent_date || null, followup_date: f.followup_date || null };
      if (isNew) await api.post("/ecoles/offers", payload); else await api.put(`/ecoles/offers/${offer.id}`, payload);
      toast.success("Offre enregistrée ✓"); onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async () => { try { await api.delete(`/ecoles/offers/${offer.id}`); toast.success("Supprimée"); onChanged(); onClose(); } catch { toast.error("Erreur"); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-zinc-950 border-zinc-800" data-testid="offer-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Nouvelle offre" : "Offre"}</DialogTitle><DialogDescription>Suivi de l'offre envoyée à l'école.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="École"><Sel value={f.school_id} onChange={(v) => set("school_id", v)} opts={Object.fromEntries(schools.map((s) => [s.id, s.name]))} placeholder="— Choisir —" testid="offer-school" /></Field>
          <Field label="Niveau"><Sel value={f.level} onChange={(v) => set("level", v)} opts={{ primaire: "Primaire", secondaire: "Secondaire", postsecondaire: "Postsecondaire / Cégep" }} /></Field>
          <Field label="Programme"><Input value={f.program} onChange={(e) => set("program", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Horaire"><Input value={f.schedule} onChange={(e) => set("schedule", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Prix (CAD)"><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="bg-zinc-900 border-zinc-800" data-testid="offer-price" /></Field>
          <Field label="Inscriptions min."><Input type="number" value={f.min_students} onChange={(e) => set("min_students", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Statut"><Sel value={f.status} onChange={(v) => set("status", v)} opts={OFFER_STATUS} testid="offer-status" /></Field>
          <Field label="Date d'envoi"><Input type="date" value={f.sent_date || ""} onChange={(e) => set("sent_date", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
        </div>
        <Field label="Notes"><textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm" /></Field>
        <DialogFooter className="gap-2">
          {!isNew && <Button onClick={del} variant="outline" className="border-red-900 text-red-400 hover:bg-red-950 mr-auto">Supprimer</Button>}
          <Button onClick={save} className="bg-red-600 hover:bg-red-500" data-testid="save-offer">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────── Sessions & inscriptions ───────────────── */
const emptySession = { school_id: "", discipline: "Kickboxing / Muay Thaï", level: "primaire", day: "Mardi", time: "16 h 15 – 17 h 15", coach: "Kru Ralph", location: "Dojo Institut Impact (1218 7e Rue)", capacity: 20, weeks: 8, start_date: "", price: 99, status: "planifie", notes: "", satisfaction: "", enrollments: [] };

function SessionsTab({ sessions, schools, onChanged }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-3">
      <Button onClick={() => setEditing({ ...emptySession })} disabled={schools.length === 0} className="bg-red-600 hover:bg-red-500" data-testid="add-session-btn">+ Nouvelle session / pilote</Button>
      {sessions.length === 0 ? <p className="text-zinc-500 text-sm py-6 text-center border border-dashed border-zinc-800 rounded-xl">Aucune session.</p>
        : sessions.map((s) => (
          <div key={s.id} className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={`session-card-${s.id}`}>
            <div className="flex items-center justify-between">
              <div><div className="font-semibold">{s.school_name} · {s.discipline}</div>
                <div className="text-[12px] text-zinc-500">{SECTORS[s.level]} · {s.day} {s.time} · {s.weeks} sem · {s.coach}</div></div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300">{SESSION_STATUS[s.status]}</span>
                <span className="text-[11px] text-emerald-300">{(s.enrollments || []).length}/{s.capacity} inscrits</span>
                <Button size="sm" onClick={() => setEditing(s)} className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700" data-testid={`edit-session-${s.id}`}>Gérer</Button>
              </div>
            </div>
          </div>
        ))}
      {editing && <SessionDialog session={editing} schools={schools} onClose={() => setEditing(null)} onChanged={onChanged} />}
    </div>
  );
}

function SessionDialog({ session, schools, onClose, onChanged }) {
  const [f, setF] = useState({ ...emptySession, ...session, enrollments: session.enrollments || [] });
  const isNew = !session.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.school_id) { toast.error("Choisis une école"); return; }
    try {
      const payload = { ...f, capacity: parseInt(f.capacity) || 0, weeks: parseInt(f.weeks) || 0, price: parseFloat(f.price) || 0, start_date: f.start_date || null };
      if (isNew) await api.post("/ecoles/sessions", payload); else await api.put(`/ecoles/sessions/${session.id}`, payload);
      toast.success("Session enregistrée ✓"); onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async () => { if (!window.confirm("Supprimer cette session ?")) return; try { await api.delete(`/ecoles/sessions/${session.id}`); toast.success("Supprimée"); onChanged(); onClose(); } catch { toast.error("Erreur"); } };
  const addEnr = () => set("enrollments", [...f.enrollments, { student_name: "", parent_name: "", parent_email: "", parent_phone: "", consent: false, payment_status: "impaye", equipment_note: "" }]);
  const setEnr = (i, k, v) => set("enrollments", f.enrollments.map((e, j) => j === i ? { ...e, [k]: v } : e));
  const rmEnr = (i) => set("enrollments", f.enrollments.filter((_, j) => j !== i));

  const payEnrollment = async (i) => {
    if (isNew) { toast.error("Enregistre d'abord la session"); return; }
    if (!f.enrollments[i]?.student_name) { toast.error("Nom de l'élève requis"); return; }
    try {
      await api.put(`/ecoles/sessions/${session.id}`, { ...f, capacity: parseInt(f.capacity) || 0, weeks: parseInt(f.weeks) || 0, price: parseFloat(f.price) || 0, start_date: f.start_date || null });
      const { data } = await api.post(`/ecoles/sessions/${session.id}/enrollment-payment-link`, { enrollment_index: i, send_email: true });
      toast.success(data.email_sent ? "✉️ Lien de paiement envoyé au parent" : "Lien de paiement créé");
      if (data.checkout_url) window.open(data.checkout_url, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur Stripe"); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const [attDate, setAttDate] = useState(today);
  const savedAtt = (session.attendance || []).find((a) => a.date === attDate);
  const [present, setPresent] = useState(() => {
    const m = {}; (savedAtt?.records || []).forEach((r) => { m[r.student_name] = r.present; }); return m;
  });
  const saveAttendance = async () => {
    if (isNew) { toast.error("Enregistre d'abord la session"); return; }
    const records = f.enrollments.filter((e) => e.student_name).map((e) => ({ student_name: e.student_name, present: !!present[e.student_name] }));
    try {
      await api.post(`/ecoles/sessions/${session.id}/attendance`, { date: attDate, records });
      toast.success(`✅ Présences enregistrées (${records.filter((r) => r.present).length}/${records.length})`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const [incidents, setIncidents] = useState(session.incidents || []);
  const [newInc, setNewInc] = useState({ date: today, type: "incident", description: "", severity: "faible" });
  const addIncident = async () => {
    if (isNew) { toast.error("Enregistre d'abord la session"); return; }
    if (!newInc.description.trim()) { toast.error("Description requise"); return; }
    try {
      const { data } = await api.post(`/ecoles/sessions/${session.id}/incident`, newInc);
      setIncidents(data.incidents); setNewInc({ date: today, type: "incident", description: "", severity: "faible" });
      toast.success("Noté ✓"); onChanged();
    } catch { toast.error("Erreur"); }
  };
  const rmIncident = async (id) => {
    try { const { data } = await api.delete(`/ecoles/sessions/${session.id}/incident/${id}`); setIncidents(data.incidents); onChanged(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="session-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Nouvelle session" : `${f.school_name || ""} · ${f.discipline}`}</DialogTitle><DialogDescription>Session parascolaire et inscriptions.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <Field label="École"><Sel value={f.school_id} onChange={(v) => set("school_id", v)} opts={Object.fromEntries(schools.map((s) => [s.id, s.name]))} placeholder="— Choisir —" testid="session-school" /></Field>
          <Field label="Niveau"><Sel value={f.level} onChange={(v) => set("level", v)} opts={{ primaire: "Primaire", secondaire: "Secondaire", postsecondaire: "Postsecondaire / Cégep" }} /></Field>
          <Field label="Statut"><Sel value={f.status} onChange={(v) => set("status", v)} opts={SESSION_STATUS} testid="session-status" /></Field>
          <Field label="Discipline"><Input value={f.discipline} onChange={(e) => set("discipline", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Jour"><Input value={f.day} onChange={(e) => set("day", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Heure"><Input value={f.time} onChange={(e) => set("time", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Coach"><Input value={f.coach} onChange={(e) => set("coach", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Capacité"><Input type="number" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Semaines"><Input type="number" value={f.weeks} onChange={(e) => set("weeks", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Date début"><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Prix/élève (CAD)"><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Lieu"><Input value={f.location} onChange={(e) => set("location", e.target.value)} className="bg-zinc-900 border-zinc-800" /></Field>
          <Field label="Satisfaction (1–5)"><Sel value={String(f.satisfaction ?? "")} onChange={(v) => set("satisfaction", v ? parseInt(v) : null)} opts={{ "": "—", "1": "1", "2": "2", "3": "3", "4": "4", "5": "5" }} testid="session-satisfaction" /></Field>
        </div>

        <div className="border-t border-zinc-800 pt-3">
          <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold text-zinc-400">Inscriptions ({f.enrollments.length})</span>
            <Button size="sm" onClick={addEnr} className="h-6 text-[11px] bg-zinc-800 hover:bg-zinc-700" data-testid="add-enrollment">+ Élève</Button></div>
          {f.enrollments.map((e, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center">
              <Input placeholder="Élève" value={e.student_name} onChange={(ev) => setEnr(i, "student_name", ev.target.value)} className="col-span-3 bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <Input placeholder="Parent" value={e.parent_name} onChange={(ev) => setEnr(i, "parent_name", ev.target.value)} className="col-span-2 bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <Input placeholder="Courriel parent" value={e.parent_email} onChange={(ev) => setEnr(i, "parent_email", ev.target.value)} className="col-span-2 bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <Input placeholder="Tél" value={e.parent_phone} onChange={(ev) => setEnr(i, "parent_phone", ev.target.value)} className="col-span-2 bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <button onClick={() => setEnr(i, "payment_status", e.payment_status === "paye" ? "impaye" : "paye")} className={`col-span-1 h-8 rounded text-[10px] ${e.payment_status === "paye" ? "bg-emerald-600" : "bg-zinc-700"}`} title="Statut paiement" data-testid={`enroll-paystatus-${i}`}>{e.payment_status === "paye" ? "Payé" : "Dû"}</button>
              <button onClick={() => payEnrollment(i)} disabled={e.payment_status === "paye"} className="col-span-1 h-8 rounded text-[12px] bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40" title="Envoyer lien de paiement Stripe au parent" data-testid={`enroll-pay-${i}`}>💳</button>
              <button onClick={() => rmEnr(i)} className="col-span-1 text-red-400">✕</button>
            </div>
          ))}
        </div>

        {/* Présences */}
        {!isNew && f.enrollments.some((e) => e.student_name) && (
          <div className="border-t border-zinc-800 pt-3" data-testid="attendance-section">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-zinc-400">📋 Présences</span>
              <div className="flex items-center gap-2">
                <Input type="date" value={attDate} onChange={(e) => setAttDate(e.target.value)} className="bg-zinc-900 border-zinc-800 h-8 text-xs w-36" data-testid="attendance-date" />
                <Button size="sm" onClick={saveAttendance} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-500" data-testid="save-attendance">Enregistrer</Button>
                <Button size="sm" onClick={() => openBlob(`/ecoles/sessions/${session.id}/attendance.csv`, "presences.csv", true)} className="h-7 text-[11px] bg-zinc-800 hover:bg-zinc-700" data-testid="export-attendance">📤 CSV</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {f.enrollments.filter((e) => e.student_name).map((e, i) => (
                <label key={i} className="flex items-center gap-2 text-sm text-zinc-300 bg-zinc-900 rounded px-2 py-1">
                  <input type="checkbox" checked={!!present[e.student_name]} onChange={(ev) => setPresent((p) => ({ ...p, [e.student_name]: ev.target.checked }))} data-testid={`present-${i}`} />
                  {e.student_name}
                </label>
              ))}
            </div>
          </div>
        )}

        {!isNew && f.enrollments.some((e) => e.student_name) && <CycleReportPanel session={session} />}

        {!isNew && (
          <div className="border-t border-zinc-800 pt-3" data-testid="incidents-section">
            <div className="text-xs font-semibold text-zinc-400 mb-2">⚠️ Suivi qualité & incidents</div>
            <div className="grid grid-cols-12 gap-1.5 mb-2 items-center">
              <Input type="date" value={newInc.date} onChange={(e) => setNewInc((p) => ({ ...p, date: e.target.value }))} className="col-span-3 bg-zinc-900 border-zinc-800 h-8 text-xs" />
              <select value={newInc.type} onChange={(e) => setNewInc((p) => ({ ...p, type: e.target.value }))} className="col-span-2 bg-zinc-900 border border-zinc-800 rounded h-8 text-xs px-1" data-testid="incident-type">
                {["incident", "blessure", "absence", "comportement", "note"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <Input placeholder="Description" value={newInc.description} onChange={(e) => setNewInc((p) => ({ ...p, description: e.target.value }))} className="col-span-4 bg-zinc-900 border-zinc-800 h-8 text-xs" data-testid="incident-desc" />
              <select value={newInc.severity} onChange={(e) => setNewInc((p) => ({ ...p, severity: e.target.value }))} className="col-span-2 bg-zinc-900 border border-zinc-800 rounded h-8 text-xs px-1">
                {["faible", "moyen", "eleve"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <Button size="sm" onClick={addIncident} className="col-span-1 h-8 text-[11px] bg-amber-600 hover:bg-amber-500 p-0" data-testid="add-incident">+</Button>
            </div>
            {incidents.map((inc) => (
              <div key={inc.id} className="flex items-center justify-between text-sm bg-zinc-900 rounded px-2 py-1 mb-1">
                <span className="text-zinc-300"><span className="text-zinc-500">{inc.date} · {inc.type} · {inc.severity} —</span> {inc.description}</span>
                <button onClick={() => rmIncident(inc.id)} className="text-red-400 text-xs">✕</button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          {!isNew && <Button onClick={del} variant="outline" className="border-red-900 text-red-400 hover:bg-red-950 mr-auto">Supprimer</Button>}
          <Button onClick={save} className="bg-red-600 hover:bg-red-500" data-testid="save-session">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────── Pilotes actifs (taux de remplissage) ───────────────── */
function PilotesTab({ sessions }) {
  const actifs = sessions.filter((s) => s.status === "actif");
  if (actifs.length === 0)
    return <p className="text-zinc-500 text-sm py-8 text-center border border-dashed border-zinc-800 rounded-xl">Aucun pilote actif. Passe une session au statut « Actif » pour la suivre ici.</p>;
  return (
    <div className="space-y-3" data-testid="pilotes-tab">
      {actifs.map((s) => {
        const cap = Number(s.capacity || 0);
        const enr = (s.enrollments || []).length;
        const paid = (s.enrollments || []).filter((e) => e.payment_status === "paye").length;
        const fill = cap ? Math.min(100, Math.round((enr / cap) * 100)) : 0;
        const seances = (s.attendance || []).length;
        const incidents = (s.incidents || []).length;
        return (
          <div key={s.id} className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={`pilote-${s.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">{s.school_name} · {s.discipline}</div>
              <div className="text-[12px] text-zinc-500">{s.day} {s.time} · {s.coach}</div>
            </div>
            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden mb-2">
              <div className={`h-full ${fill >= 100 ? "bg-emerald-500" : fill >= 50 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${fill}%` }} />
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <span className="text-zinc-300">Remplissage : <b>{enr}/{cap}</b> ({fill}%)</span>
              <span className="text-emerald-300">Payés : {paid}/{enr}</span>
              <span className="text-zinc-400">Séances suivies : {seances}</span>
              <span className={incidents ? "text-amber-300" : "text-zinc-500"}>Incidents : {incidents}</span>
              <span className="text-zinc-400">Revenu payé : {fmt(paid * Number(s.price || 0))}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────── Kit de vente ───────────────── */
function KitTab() {
  const [kit, setKit] = useState(null);
  useEffect(() => { api.get("/ecoles/kit").then((r) => setKit(r.data)).catch(() => {}); }, []);
  if (!kit) return <div className="text-zinc-500">Chargement…</div>;
  return (
    <div className="space-y-4" data-testid="kit-tab">
      <Card title="📧 Courriel d'approche" onCopy={() => copy(`${kit.email.objet}\n\n${kit.email.corps}`)}>
        <div className="text-xs text-zinc-500 mb-1">Objet : {kit.email.objet}</div>
        <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans">{kit.email.corps}</pre>
      </Card>
      <Card title="📞 Script d'appel" onCopy={() => copy(kit.script_appel.join("\n\n"))}>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-300">{kit.script_appel.map((s, i) => <li key={i}>{s}</li>)}</ol>
      </Card>
      <Card title="💪 Arguments clés" onCopy={() => copy(kit.arguments.join("\n"))}>
        <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-300">{kit.arguments.map((a, i) => <li key={i}>{a}</li>)}</ul>
      </Card>
      <Card title="💰 Tarifs suggérés">
        <div className="space-y-2">{kit.tarifs.map((t, i) => (
          <div key={i} className="flex justify-between gap-3 text-sm border-b border-zinc-900 pb-1.5">
            <div><span className="font-medium text-white">{t.formule}</span><div className="text-[12px] text-zinc-500">{t.details}</div></div>
            <div className="text-emerald-300 whitespace-nowrap">{t.prix}</div>
          </div>))}</div>
      </Card>
      <Card title="📋 Format recommandé">
        <div className="grid grid-cols-2 gap-2 text-sm text-zinc-300">
          {Object.entries(kit.format).map(([k, v]) => <div key={k}><span className="text-zinc-500 capitalize">{k} :</span> {v}</div>)}
        </div>
      </Card>

      {kit.note_rencontre && (
        <Card title="📝 Template — Note de rencontre école" onCopy={() => copy(kit.note_rencontre)}>
          <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans">{kit.note_rencontre}</pre>
        </Card>
      )}

      {kit.email_en && (
        <div className="pt-2" data-testid="kit-en-section">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">🇬🇧 Version anglaise (écoles anglophones — ex. Golden Valley)</div>
          <div className="space-y-4">
            <Card title="📧 Approach email (EN)" onCopy={() => copy(`${kit.email_en.objet}\n\n${kit.email_en.corps}`)}>
              <div className="text-xs text-zinc-500 mb-1">Subject: {kit.email_en.objet}</div>
              <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-sans">{kit.email_en.corps}</pre>
            </Card>
            <Card title="📞 Call script (EN)" onCopy={() => copy(kit.script_appel_en.join("\n\n"))}>
              <ol className="list-decimal pl-5 space-y-1.5 text-sm text-zinc-300">{kit.script_appel_en.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </Card>
            <Card title="💪 Key points (EN)" onCopy={() => copy(kit.arguments_en.join("\n"))}>
              <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-300">{kit.arguments_en.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children, onCopy }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-zinc-200">{title}</div>
        {onCopy && <Button size="sm" onClick={onCopy} className="h-7 text-[11px] bg-zinc-800 hover:bg-zinc-700">📋 Copier</Button>}
      </div>
      {children}
    </div>
  );
}

/* ───────────────── petits helpers UI ───────────────── */
function Field({ label, children }) {
  return <div><label className="text-[11px] text-zinc-500">{label}</label>{children}</div>;
}
function Sel({ value, onChange, opts, placeholder, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm">
      {placeholder && <option value="">{placeholder}</option>}
      {Object.entries(opts).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
}
